import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Label,
  Row,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import ReactDatePicker from "react-datepicker";
import Select from "react-select";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
 import { customReactSelectStyles } from "../../../styles/ReactSelectStyles";
import { TaxTypeFormValues } from "../../../types/tax-types/TaxTypes";
import { calculationTypeOptions, countryOptions } from "../../../service";
import SlabSection from "./SlabSection";
import { validationSchema } from "./validationTaxType";
import NavigationBreadcrumbs from "../../../common-elements/breadcrumbs/NavigationBreadcrumbs";

const AddTaxTypeForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const countryName = queryParams.get("country")?.replace(/%20/g, " ") || "";
  const taxType = queryParams.get("taxType");
  const isEditMode = !!taxType;

  const selectedCountry = countryOptions.find(
    (option) => option.label.toLowerCase() === countryName.toLowerCase()
  );

  const initialValues: TaxTypeFormValues = {
    tax_type_code: "",
    tax_type_name: "",
    calculation_type: "",
    applicable_countries: selectedCountry
      ? [
          {
            country_id: selectedCountry.value,
            country_active: true,
            effective_from: new Date().toISOString().slice(0, 10),
            effective_to: null,
            fixed_amount: null,
            percentage_rate: null,
            calculation_type: "fixed",
            slab_rule: null,
          },
        ]
      : [],
    fixed_amount: null,
    percentage_rate: null,
    slab_rule: null,
    tax_type_description: "",
    effective_from: null,
    effective_to: null,
  };

  const handleSubmit = (values: TaxTypeFormValues) => {
    const payload = {
      tax_type_code: values.tax_type_code,
      tax_type_name: values.tax_type_name,
      tax_type_description: values.tax_type_description,
      effective_from: values.effective_from,
      effective_to: values.effective_to,
      applicable_countries: values.applicable_countries.map((country) => ({
        country_id: country.country_id,
        country_active: country.country_active,
        effective_from: country.effective_from,
        effective_to: country.effective_to,
        fixed_amount:
          values.calculation_type === "fixed" ? values.fixed_amount : null,
        percentage_rate:
          values.calculation_type === "percentage"
            ? values.percentage_rate
            : null,
        calculation_type: values.calculation_type,
        slab_rule: values.calculation_type === "slab" ? values.slab_rule : null,
      })),
    };
    navigate(`${process.env.PUBLIC_URL}/masterdata/taxtypes`);
  };

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const breadcrumbLinks = [
    { label: "Tax Types", url: `${process.env.PUBLIC_URL}/masterdata/taxtype` },
    { label: isEditMode ? "Edit Tax Type" : "Add Tax Types", active: true },
    ...(countryName ? [{ label: t(countryName), active: true }] : []),
  ];


  return (
    <>
      <NavigationBreadcrumbs
        mainTitle={isEditMode ? t("EditTaxType") : t("AddTaxTypes")}
        parent={t("Master Data")}
        links={breadcrumbLinks}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    setFieldValue,
                    values,
                    errors,
                    touched,
                    setFieldTouched,
                  }) => {
                    return (
                      <Form className="theme-form">
                        <Row className="g-3">
                          <Col md={6}>
                            <FormGroup>
                              <Label className="form-label">
                                {t("TaxName")} <span className="txt-danger">*</span>
                              </Label>
                              <Field
                                name="tax_type_name"
                                type="text"
                                className="form-control"
                                placeholder="E.g. Land Tax"
                              />
                              <div>
                                <ErrorMessage
                                  name="tax_type_name"
                                  component="span"
                                  className="text-danger mt-1 d-block"
                                />
                              </div>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label className="form-label">
                                {t("TaxCode")}<span className="txt-danger">*</span>
                              </Label>
                              <Field
                                name="tax_type_code"
                                type="text"
                                className="form-control"
                                placeholder="E.g. LBTT"
                              />
                              <div>
                                <ErrorMessage
                                  name="tax_type_code"
                                  component="span"
                                  className="text-danger mt-1 d-block"
                                />
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row className="g-3">
                          <Col md={12}>
                            <FormGroup>
                              <Label className="form-label">
                                {t("Country")}<span className="txt-danger">*</span>
                              </Label>
                              <Select
                                isMulti
                                name="applicable_countries"
                                options={countryOptions}
                                placeholder="Select Countries"
                                classNamePrefix="select"
                                styles={customReactSelectStyles}
                                value={countryOptions.filter((opt) =>
                                  values.applicable_countries.some(
                                    (c) => c.country_id === opt.value
                                  )
                                )}
                                onChange={(selectedOptions) => {
                                  const selected = selectedOptions || [];
                                  const newCountries = selected.map((opt) => {
                                    const existing =
                                      values.applicable_countries.find(
                                        (c) => c.country_id === opt.value
                                      );
                                    return (
                                      existing || {
                                        country_id: opt.value,
                                        country_active: true,
                                        effective_from: new Date()
                                          .toISOString()
                                          .slice(0, 10),
                                        effective_to: null,
                                      }
                                    );
                                  });
                                  setFieldValue(
                                    "applicable_countries",
                                    newCountries
                                  );
                                }}
                                formatOptionLabel={(option) => (
                                  <div className="d-flex align-items-center">
                                    {option.flag && (
                                      <img
                                        src={`/assets/fonts/flag-icon/${option.flag}.svg`}
                                        alt={option.label}
                                        style={{
                                          width: 20,
                                          height: 15,
                                          marginRight: 8,
                                        }}
                                      />
                                    )}
                                    <span>{option.label}</span>
                                  </div>
                                )}
                                isDisabled={!!selectedCountry}
                              />
                              <div>
                                <ErrorMessage
                                  name="applicable_countries"
                                  component="span"
                                  className="text-danger mt-1 d-block"
                                />
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row className="g-3">
                          <Col md={4}>
                            <FormGroup>
                              <Label className="form-label">
                                {t("CalculationType")} 
                                <span className="txt-danger">*</span>
                              </Label>
                              <Select
                                name="calculation_type"
                                options={calculationTypeOptions}
                                value={calculationTypeOptions.find(
                                  (opt) => opt.value === values.calculation_type
                                )}
                                onChange={(selected) => {
                                  setFieldValue(
                                    "calculation_type",
                                    selected?.value || ""
                                  );
                                  setFieldValue("fixed_amount", null);
                                  setFieldValue("percentage_rate", null);
                                  setFieldValue("slab_rule", null);
                                }}
                                classNamePrefix="select"
                                styles={customReactSelectStyles}
                                placeholder="Select Calculation Type"
                              />
                              <div>
                                <ErrorMessage
                                  name="calculation_type"
                                  component="span"
                                  className="text-danger mt-1 d-block"
                                />
                              </div>
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup className="d-flex flex-column align-items-stretch">
                              <Label className="form-label d-block">
                                {t("EffectiveFrom")}
                                <span className="txt-danger">*</span>
                              </Label>
                              <Field name="effective_from">
                                {({ field, form }: any) => (
                                  <ReactDatePicker
                                    className="form-control datepicker-here"
                                    selected={
                                      field.value ? new Date(field.value) : null
                                    }
                                    onChange={(date) =>
                                      form.setFieldValue(
                                        field.name,
                                        date
                                          ? date.toISOString().slice(0, 10)
                                          : ""
                                      )
                                    }
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="YYYY-MM-DD"
                                  />
                                )}
                              </Field>
                              <div>
                                <ErrorMessage
                                  name="effective_from"
                                  component="span"
                                  className="text-danger mt-1 d-block"
                                />
                              </div>
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup className="d-flex flex-column align-items-stretch">
                              <Label className="form-label d-block">
                                {t("EffectiveTo")}
                              </Label>
                              <Field name="effective_to">
                                {({ field, form }: any) => (
                                  <ReactDatePicker
                                    className="form-control datepicker-here"
                                    selected={
                                      field.value ? new Date(field.value) : null
                                    }
                                    onChange={(date) =>
                                      form.setFieldValue(
                                        field.name,
                                        date
                                          ? date.toISOString().slice(0, 10)
                                          : ""
                                      )
                                    }
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="YYYY-MM-DD"
                                  />
                                )}
                              </Field>
                              <div>
                                <ErrorMessage
                                  name="effective_to"
                                  component="span"
                                  className="text-danger mt-1 d-block"
                                />
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>

                        {values.calculation_type === "fixed" && (
                          <Row className="g-3">
                            <Col md={4}>
                              <FormGroup>
                                <Label className="form-label">
                                  {t("FixedAmount")}
                                  <span className="txt-danger">*</span>
                                </Label>
                                <Field
                                  name="fixed_amount"
                                  type="number"
                                  min={0}
                                  className="form-control"
                                  placeholder="Enter fixed amount"
                                />
                                <div>
                                  <ErrorMessage
                                    name="fixed_amount"
                                    component="span"
                                    className="text-danger mt-1 d-block"
                                  />
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>
                        )}

                        {values.calculation_type === "percentage" && (
                          <Row className="g-3">
                            <Col md={4}>
                              <FormGroup>
                                <Label className="form-label">
                                  {t("PercentageRate")}
                                  <span className="txt-danger">*</span>
                                </Label>
                                <Field
                                  name="percentage_rate"
                                  type="number"
                                  min={0}
                                  className="form-control"
                                  placeholder="Enter percentage rate"
                                />
                                <div>
                                  <ErrorMessage
                                    name="percentage_rate"
                                    component="span"
                                    className="text-danger mt-1 d-block"
                                  />
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>
                        )}

                        {values.calculation_type === "slab" && (
                          <SlabSection
                            values={values}
                            errors={errors}
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            generateId={generateId}
                          />
                        )}

                        <Row className="g-3">
                          <Col md={12}>
                            <FormGroup className="mb-3">
                              <Label className="form-label">
                                {t("Description")}
                              </Label>
                              <Field
                                name="tax_type_description"
                                as="textarea"
                                className="form-control"
                                placeholder="Description"
                                rows={3}
                              />
                              <div style={{ minHeight: 22 }}>
                                <ErrorMessage
                                  name="tax_type_description"
                                  component="span"
                                  className="text-danger mt-1 d-block"
                                />
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>

                        <div className="text-end mt-4">
                          <Button
                            color="secondary"
                            className="me-2"
                            onClick={() =>
                              navigate(
                                `${process.env.PUBLIC_URL}/masterdata/taxtype`
                              )
                            }
                          >
                            {t("Cancel")}
                          </Button>
                          <Button color="primary" type="submit">
                            {isEditMode ? t("Modify") : t("Save")}
                          </Button>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AddTaxTypeForm;